import React, { useEffect, useState } from 'react'
import { Section } from '../components'
import api from '../api-client'
import { useParams, useLocation } from 'react-router-dom'
import { Spinner } from '@fluentui/react'
import { dollars, FundingSchedule, FUND_RELEASE_LOAN_HELP_LINK, getFundReleaseDetails, getBrowserFingerprintData } from '@oneethos/shared'
import { toast } from 'react-toastify'
import { errorToString } from '@oneethos/shared'
import FundReleaseError from './fundReleaseError'

export const FundingReleasePage = () => {
  const [pendingAgreement, setPendingAgreement] = useState(null)
  const [agreed, setAgreed] = useState<boolean>(false)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const location = useLocation()
  const { id } = useParams()
  const token = new URLSearchParams(location.search).get('t')
  const helpLink: string = FUND_RELEASE_LOAN_HELP_LINK

  const details = pendingAgreement ? getFundReleaseDetails(
    pendingAgreement.type,
    pendingAgreement.companyName,
    pendingAgreement.fundingSchedule || FundingSchedule.FiftyFifty
  ) : { description: '', requirements: [] }

  const { description, requirements } = details

  const fetchFundReleaseInfo = async () => {
    api.get(`/c/loanapps/${id}/fund-release?t=${encodeURIComponent(token)}`)
      .then((data) => {
        setPendingAgreement(data)
      })
      .catch((err) => {
        const status = err.status
        const error = err.error || err.message

        if (status === 401 && error === 'expired') {
          setErrorKey('expired')
        } else if (status === 403 && error === 'token required') {
          setErrorKey('token_required')
        } else if (status === 401 && error === 'invalid token') {
          setErrorKey('invalid_token')
        } else {
          setErrorKey('generic')
        }
      })
  }

  useEffect(() => {
    if (id) fetchFundReleaseInfo()
  }, [id])

  const handleAgree = () => {
    if (pendingAgreement) setAgreed(!agreed)
  }

  const handleApprove = async () => {
    try {
      setLoading(true)
      await api.post(
        `/c/loanapps/${id}/fund-release/${pendingAgreement._id}/approve${token ? `?t=${encodeURIComponent(token)}` : ''}`,
        { browserInfo: getBrowserFingerprintData() }
      )
      setIsSubmitted(true)
    } catch (err) {
      toast.error(errorToString(err))
    } finally {
      setLoading(false)
    }
  }

  if (errorKey) {
    return (
      <Section>
        <FundReleaseError error={errorKey} />
      </Section>
    )
  }

  if (isSubmitted) {
    return (
      <Section>
        <div className="text-center">
          <h3>Thank you!</h3>
          <p>
            Your approval has been documented. Once your installer has provided all
            necessary documentation for this milestone, we will release the approved
            funds to them.
          </p>
        </div>
      </Section>
    )
  }

  if (!pendingAgreement) {
    return (
      <Section>
        <p className="text-center">No pending fund releases found.</p>
      </Section>
    )
  }

  if (pendingAgreement.hasAgreed) {
    return (
      <Section>
        <div className="text-center">
          <h3>Fund Release Already Approved</h3>
          <p>
            You have already approved the fund release for this request. We'll notify you if any further action is required.
          </p>
        </div>
      </Section>
    )
  }

  return (
    <Section>
      <h3 className='text-center'>Fund Release Request</h3>
      <div className="mb-4">
        <p><strong>Amount:</strong> {dollars(pendingAgreement.amount)}</p>
        <p>{description}</p>
      </div>

      <div className="mb-4">
        <h5>By approving this request, you certify the following:</h5>
        <ul className="mb-4">
          {requirements.map((req, index) => (
            <li key={index} className="mb-1">
              <strong>{req.title}</strong> {req.detail}
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-4">
        <h5>Solar Project Funding and Tax Incentive Disclosure</h5>
        <p>If your intention is to proceed with your solar installation project in order to capture federal tax incentives that may soon expire, we recommend to not authorize the release of loan or funding proceeds until you have:</p>
        <ul>
          <li><b>Consulted with your accountant or tax advisor</b> to confirm your eligibility for the intended tax incentives,</li>
          <li><b>Confirmed directly with your solar installer</b> that your project will be completed within the required timeframe to qualify for those incentives.</li>
        </ul>
        <p>By approving the release of funds, you acknowledge and agree that:</p>
        <ul>
          <li><b>Climate First Bank has no control over your contract with the solar installer.</b></li>
          <li><b>Climate First Bank does not provide tax advice</b> and makes no representations regarding your eligibility for any tax incentives, rebates, or credits.</li>
          <li><b>You have independently consulted with your accountant/tax advisor and your solar installer</b> and confirmed that proceeding with the fund release is in your best interest.</li>
          <li><b>You release and hold Climate First Bank harmless</b> from any liability, loss, or damages arising from (a) delays or non-completion of the project, (b) ineligibility for any tax incentive, or (c) any other matter related to your contractual arrangement with the installer or your personal tax situation.</li>
        </ul>
        <p>By proceeding with the authorization of funds release, you acknowledge your full understanding and acceptance of the terms stated above.</p>
      </div>
      <p>
        <strong>Help Article:</strong><br />
        <a href={helpLink} target="_blank" rel="noopener noreferrer" className='mt-3'>
          Learn more about the fund release process and what it means for your project
        </a>
      </p>

      <div className="form-group text-center mt-4">
        <label className='d-flex align-items-center justify-content-center gap-2'>
          <input
            type="checkbox"
            checked={agreed}
            onChange={handleAgree}
          />
          <b>I approve the release of funds to {pendingAgreement.companyName}.</b>
        </label>
      </div>

      <div className='text-center mt-3'>
        <button
          className="btn btn-primary"
          disabled={!agreed}
          onClick={handleApprove}
        >
          {loading ? <Spinner /> : 'Approve Request'}
        </button>
      </div>
    </Section>
  )
}

export default FundingReleasePage
